import { Picture } from '@app/components/Picture';
import { Box, Button, constants } from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import { DeviceType } from '@redbubble/next/types';
import React from 'react';
import { getUrlWithLocale } from '../../utils/getUrlWithLocale';
import { useContentfulImage } from '../../utils/useContentfulImage';
import {
  desktopImageSizes,
  mobileLargeTileImageSizes,
  mobileSmallTileImageSizes,
} from './imageSizes';
import styles from './Tile.module.scss';

interface TileProps {
  url?: string | null;
  name?: string | null;
  locale: string;
  callToActionText?: string | null;
  desktopImage?: string | null;
  mobileImage?: string | null;
  index: number;
}

const Tile = React.memo(
  ({
    url,
    name,
    locale,
    callToActionText,
    desktopImage,
    mobileImage,
    index,
  }: TileProps) => {
    const { isOrAbove } = useViewportWidth();
    const isDesktop = isOrAbove(DeviceType.Desktop);
    const mobileTileSize = index === 2 || index === 7 ? 'large' : 'small';
    const mobileImageSizes =
      mobileTileSize === 'large'
        ? mobileLargeTileImageSizes
        : mobileSmallTileImageSizes;

    const desktopImageProps = useContentfulImage(
      desktopImageSizes,
      desktopImage
    );

    const mobileImageProps = useContentfulImage(mobileImageSizes, mobileImage);

    return (
      <Box
        key={url}
        className={[
          styles.homePageTile,
          mobileTileSize === 'small' && styles.pathway,
          mobileTileSize === 'large' && styles.pathwayLarge,
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={`tile-${index}`}
      >
        <a
          href={getUrlWithLocale(locale, url)}
          className={styles.imageLink}
          tabIndex={-1}
        >
          <Picture roundedCorners fillContainer>
            <Picture.Source
              srcSet={desktopImageProps.srcSet}
              type="image/jpeg"
              media="(min-width: 769px)"
              sizes={desktopImageProps.sizes}
              testId={`pathway-img-desktop-${index}`}
            />
            <Picture.Source
              srcSet={mobileImageProps.srcSet}
              type="image/jpeg"
              media="(max-width: 768px)"
              sizes={mobileImageProps.sizes}
              testId={`pathway-img-mobile-${index}`}
            />
            <Picture.Img src={desktopImageProps.src} alt={name ?? 'tile'} />
          </Picture>
        </a>
        <Box className={styles.buttonWrapper}>
          <Button
            href={getUrlWithLocale(locale, url)}
            className={styles.tileButton}
            size={isDesktop ? constants.MEDIUM : constants.SMALL}
            elevation={isDesktop ?? constants.ELEVATION_MEDIUM}
            strong={!!isDesktop}
            fluid={!isDesktop}
            style={{
              whiteSpace: isDesktop ? 'nowrap' : 'normal',
              lineHeight: isDesktop ? '' : 'normal',
              textAlign: isDesktop ? 'left' : 'center',
            }}
          >
            {callToActionText}
          </Button>
        </Box>
      </Box>
    );
  }
);

export { Tile };
